// G360_K470 - sort, filter, column choice and CSV export (the shared DataTable).
// G360_K462 - a failed read is said, not shown as an empty list or a zero; a refused change is said, not shown as done.
// Tenants - full list, secure impersonation, + CREATE ORGANIZATION (provision)
// with dual invite result: copy link + send email.
import { useEffect, useState } from 'react';
import { Building2, Eye, Plus, X, Copy, Check, Mail } from 'lucide-react';
import { listTenants, listActiveGrants, startImpersonation, revokeGrant } from '../lib/adminApi.js';
import { provisionOrg, sendInviteEmail } from '../lib/adminOrgApi.js';
import OrgManageModal from '../components/OrgManageModal.jsx';
import { EmptyState } from '@govern360/shared/components/ui.jsx';
import { DataTable } from '@govern360/shared/components/datatable.jsx';

const CONSOLE_URL = 'https://console.govern360.ai';

const TENANT_COLS = [
  { key: 'name', label: 'Tenant', value: (t) => t.name, csv: (t) => t.name,
    render: (t) => <><strong>{t.name}</strong><div style={{ fontSize: 11, color: 'var(--g-text-2)' }}>{t.slug}</div></> },
  { key: 'slug', label: 'Slug', render: (t) => <span className="mono" style={{ fontSize: 11 }}>{t.slug}</span> },
  { key: 'industry', label: 'Industry', render: (t) => <span style={{ color: 'var(--g-text-2)' }}>{t.industry || '-'}</span> },
  { key: 'size', label: 'Size', render: (t) => <span style={{ color: 'var(--g-text-2)' }}>{t.size || '-'}</span> },
  { key: 'plan', label: 'Plan', render: (t) => <span className="pill p-info">{t.plan}</span> },
  { key: 'subscription_status', label: 'Status', value: (t) => t.subscription_status || 'trialing',
    render: (t) => <span className={`pill ${t.subscription_status === 'active' ? 'p-succ' : 'p-gray'}`}>{t.subscription_status || 'trialing'}</span> },
  { key: 'seats', label: 'Seats', align: 'right', value: (t) => (t.seats == null ? null : Number(t.seats)) },
  { key: 'created_at', label: 'Joined', value: (t) => (t.created_at ? new Date(t.created_at).toLocaleDateString() : null),
    sort: (t) => (t.created_at ? new Date(t.created_at).getTime() : null), csv: (t) => t.created_at || '' },
];

export default function TenantsPage() {
  const [tenants, setTenants] = useState([]);
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', industry: '', size: 'SMB', plan: 'trial', ownerEmail: '' });
  const [creating, setCreating] = useState(false);
  const [manageOrg, setManageOrg] = useState(null);
  const [err, setErr] = useState('');
  const [loadErr, setLoadErr] = useState('');

  // invite result state
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendErr, setSendErr] = useState('');

  async function refresh() {
    setLoading(true);
    const [{ data: t, error: te }, { data: g, error: ge }] = await Promise.all([listTenants(), listActiveGrants()]);
    const bad = te || ge;
    setLoadErr(bad ? ((te ? 'tenants' : 'impersonation grants') + ': ' + (bad.message || 'the read failed')) : '');
    setTenants(t); setGrants(g);
    setLoading(false);
  }
  useEffect(() => { refresh(); }, []);

  async function impersonate(t) {
    const reason = prompt(`Impersonate ${t.name} as their tenant admin.\n\nAll actions are logged on both sides. Session expires in 30 minutes.\n\nReason (required):`);
    if (!reason || reason.trim().length < 3) return;
    setBusy(true);
    const { url, error } = await startImpersonation(t.id, reason.trim(), CONSOLE_URL);
    setBusy(false);
    if (error) { alert(error.message); return; }
    window.open(url, '_blank', 'noopener');
    refresh();
  }
  async function revoke(g) {
    if (!confirm(`End impersonation of ${tenantName(g.tenant_id)}?`)) return;
    // G360_K462 - an impersonation that was not ended must stay listed.
    const { error } = await revokeGrant(g.id);
    if (error) { alert('Impersonation NOT ended: ' + (error.message || 'refused') + '. It is still active.'); return; }
    setGrants((prev) => prev.filter((x) => x.id !== g.id));
  }
  function tenantName(id) { return tenants.find((t) => t.id === id)?.name || id; }

  function openCreate() {
    setForm({ name: '', industry: '', size: 'SMB', plan: 'trial', ownerEmail: '' });
    setErr(''); setResult(null); setCopied(false); setSent(false); setSendErr('');
    setShowCreate(true);
  }
  function closeCreate() { setShowCreate(false); setResult(null); }

  async function create() {
    setErr('');
    if (!form.name.trim()) { setErr('Organization name is required.'); return; }
    setCreating(true);
    const { data, error } = await provisionOrg(form);
    setCreating(false);
    if (error) { setErr(error.message); return; }
    setResult(data);
    refresh();
  }

  const acceptUrl = result && result.invitation_token
    ? `${CONSOLE_URL}/accept-invite?token=${result.invitation_token}` : '';

  async function copyLink() {
    try { await navigator.clipboard.writeText(acceptUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch (e) { /* clipboard blocked; user can select the field */ }
  }
  async function emailInvite() {
    if (!result || !result.invitation_id) return;
    setSending(true); setSendErr('');
    const { error } = await sendInviteEmail(result.invitation_id);
    setSending(false);
    if (error) { setSendErr(error.message || 'Send failed'); return; }
    setSent(true);
  }

  return (
    <div>
      <div className="ph">
        <div>
          <h1 className="ph-h1">Tenants</h1>
          <p className="ph-sub">Every customer org on the platform. Create, drill in, or impersonate (logged).</p>
        </div>
        <div className="ph-cta">
          <button className="btn admin-primary" onClick={openCreate}><Plus size={18} />Create organization</button>
        </div>
      </div>

      <div className="banner admin">
        <Eye size={18} />
        <span>"Create organization" provisions a new tenant and optionally invites its first owner. "Open as admin" mints a logged 30-minute impersonation session.</span>
      </div>

      {grants.length > 0 && (
        <div className="card pad-0" style={{ marginBottom: 16 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--g-divider)', fontSize: 13, fontWeight: 500 }}>Active impersonation sessions</div>
          <table className="tb">
            <thead><tr><th>Tenant</th><th>Reason</th><th>Expires</th><th></th></tr></thead>
            <tbody>
              {grants.map((g) => (
                <tr key={g.id}>
                  <td><strong>{tenantName(g.tenant_id)}</strong></td>
                  <td style={{ color: 'var(--g-text-2)', fontSize: 12 }}>{g.reason}</td>
                  <td style={{ fontSize: 12 }}>{new Date(g.expires_at).toLocaleTimeString()}</td>
                  <td><button className="btn outline" style={{ height: 28, padding: '0 10px', fontSize: 11 }} onClick={() => revoke(g)}><X size={14} />End</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {loading ? (
        <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--g-text-2)' }}>Loading...</div>
      ) : loadErr ? (
        <div className="card g360-admin-err" style={{ padding: 24, borderLeft: '3px solid var(--g-danger)' }}>
          <p style={{ color: 'var(--g-danger)', fontSize: 14, margin: '0 0 8px' }}>Part of this page could not be read</p>
          <p style={{ fontSize: 13, color: 'var(--g-text-2)', margin: 0 }}>{loadErr}. This is not the same as there being none.</p>
        </div>
      ) : tenants.length === 0 ? (
        <EmptyState icon={Building2} title="No tenants yet" hint="Click 'Create organization' to provision the first one." />
      ) : (
        <DataTable rows={tenants} columns={TENANT_COLS} storageKey="g360.admin.tenants.hiddenCols" name="tenants" defaultHidden={['slug']}
          placeholder="Filter by name, slug, industry, plan or status..." rowKey={(t) => t.id}
          actions={(t) => (
            <>
              <button className="btn outline" style={{ height: 28, padding: '0 10px', fontSize: 11 }} onClick={() => impersonate(t)} disabled={busy}>
                <Eye size={14} />Open as admin
              </button>
              <button className="btn outline" style={{ height: 28, padding: '0 10px', fontSize: 11, marginLeft: 6 }} onClick={() => setManageOrg(t)}>
                Manage
              </button>
            </>
          )} />
      )}

      {manageOrg && (
        <OrgManageModal org={manageOrg} onClose={() => setManageOrg(null)} onChanged={refresh} />
      )}

      {showCreate && (
        <div className="wiz-backdrop" onClick={(e) => { if (e.target === e.currentTarget) closeCreate(); }}>
          <div className="wiz-card" style={{ maxWidth: 460 }}>
            <button className="wiz-x" onClick={closeCreate}><X size={18} /></button>

            {!result ? (
              <>
                <h2 className="wiz-h" style={{ textAlign: 'left', fontSize: 18 }}>Create organization</h2>
                <p className="wiz-p" style={{ textAlign: 'left' }}>Provision a new tenant. Optionally invite its first owner by email - if they already have an account they're attached immediately, otherwise an invitation is created.</p>

                <label className="field-l">Organization name</label>
                <input className="inp" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Acme Inc." />

                <div className="row r2" style={{ marginTop: 12 }}>
                  <div>
                    <label className="field-l">Industry</label>
                    <select className="inp" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })}>
                      <option value="">-</option>
                      <option>Fintech</option><option>Healthcare</option><option>SaaS</option>
                      <option>Manufacturing</option><option>Retail</option><option>Government</option><option>Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="field-l">Size</label>
                    <select className="inp" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })}>
                      <option value="SMB">SMB</option><option value="Mid-market">Mid-market</option><option value="Enterprise">Enterprise</option>
                    </select>
                  </div>
                </div>

                <div className="row r2" style={{ marginTop: 12 }}>
                  <div>
                    <label className="field-l">Plan</label>
                    <select className="inp" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}>
                      <option value="trial">Trial</option><option value="starter">Starter</option>
                      <option value="business">Business</option><option value="enterprise">Enterprise</option>
                    </select>
                  </div>
                  <div>
                    <label className="field-l">First owner email (optional)</label>
                    <input className="inp" value={form.ownerEmail} onChange={(e) => setForm({ ...form, ownerEmail: e.target.value })} placeholder="owner@acme.com" />
                  </div>
                </div>

                {err && <div className="banner danger" style={{ marginTop: 12, marginBottom: 0 }}>{err}</div>}

                <button className="btn admin-primary block" style={{ marginTop: 16 }} onClick={create} disabled={creating || !form.name.trim()}>
                  {creating ? 'Creating...' : 'Create organization'}
                </button>
              </>
            ) : (
              <>
                <h2 className="wiz-h" style={{ textAlign: 'left', fontSize: 18 }}>Organization created</h2>
                <p className="wiz-p" style={{ textAlign: 'left' }}>
                  <strong>{result.org && result.org.name}</strong> is provisioned.
                </p>

                {result.owner_attached && (
                  <div className="banner" style={{ marginTop: 4 }}>
                    {result.owner_email} already had an account - attached as owner. No invite needed.
                  </div>
                )}

                {result.invitation_token && (
                  <>
                    <label className="field-l" style={{ marginTop: 8 }}>Invite link for {result.owner_email}</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input className="inp" readOnly value={acceptUrl} onFocus={(e) => e.target.select()} style={{ flex: 1 }} />
                      <button className="btn outline" onClick={copyLink} style={{ whiteSpace: 'nowrap' }}>
                        {copied ? <><Check size={16} />Copied</> : <><Copy size={16} />Copy</>}
                      </button>
                    </div>

                    <button className="btn admin-primary block" style={{ marginTop: 12 }} onClick={emailInvite} disabled={sending || sent}>
                      {sent ? <><Check size={16} />Email sent to {result.owner_email}</> : sending ? 'Sending...' : <><Mail size={16} />Send invite email</>}
                    </button>
                    {sendErr && <div className="banner danger" style={{ marginTop: 8, marginBottom: 0 }}>{sendErr}</div>}
                    <p style={{ fontSize: 12, color: 'var(--g-text-2)', marginTop: 10 }}>Link expires in 14 days. Copy works even if email is unavailable.</p>
                  </>
                )}

                {!result.invitation_token && !result.owner_attached && (
                  <div className="banner" style={{ marginTop: 4 }}>
                    No owner email provided. Invite users later from the org's Users and roles page.
                  </div>
                )}

                <button className="btn outline block" style={{ marginTop: 12 }} onClick={closeCreate}>Done</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
